import React, { useState, useMemo } from "react";
import { api, formatDateBR } from "../lib/api";
import { toast } from "sonner";
import { X, Loader2, Calendar } from "lucide-react";

/**
 * Modal para "Alterar vencimento" do contrato.
 * Dois modos:
 *  - monthly: altera o dia mensal fixo do contrato (afeta próximas parcelas).
 *  - current_cycle: altera SOMENTE a due_date do ciclo atual (parcela específica).
 */
export default function ChangeDueDayModal({ contract, onClose, onDone }) {
  const cc = contract?.current_cycle || {};
  const currentDueDate = cc.due_date || null;
  const currentVencDay = Number(contract?.vencimento_day ?? 0);

  const [mode, setMode] = useState("monthly"); // 'monthly' | 'current_cycle'
  const [newVencDay, setNewVencDay] = useState(String(currentVencDay || ""));
  const [applyToCurrent, setApplyToCurrent] = useState(true);
  const [newDueDate, setNewDueDate] = useState(currentDueDate || "");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  // Preview do novo vencimento no modo monthly
  const previewMonthly = useMemo(() => {
    if (mode !== "monthly") return null;
    const day = parseInt(newVencDay, 10);
    if (!Number.isFinite(day) || day < 1 || day > 31) return null;
    if (!currentDueDate) return { day, newDate: null };
    if (!applyToCurrent) return { day, newDate: currentDueDate };
    const d = new Date(currentDueDate + "T00:00:00");
    const year = d.getFullYear();
    const month = d.getMonth();
    const lastDay = new Date(year, month + 1, 0).getDate();
    const capped = Math.min(day, lastDay);
    const iso = `${year}-${String(month + 1).padStart(2, "0")}-${String(capped).padStart(2, "0")}`;
    return { day, newDate: iso, capped: capped !== day };
  }, [mode, newVencDay, currentDueDate, applyToCurrent]);

  const submit = async (e) => {
    e.preventDefault();
    if (reason.trim().length < 5) {
      toast.error("Motivo precisa ter ao menos 5 caracteres");
      return;
    }
    const body = { mode, reason: reason.trim() };
    if (mode === "monthly") {
      const day = parseInt(newVencDay, 10);
      if (!Number.isFinite(day) || day < 1 || day > 31) {
        toast.error("Dia de vencimento inválido (1-31)");
        return;
      }
      body.new_vencimento_day = day;
      body.apply_to_current_cycle = !!applyToCurrent;
    } else {
      if (!newDueDate) {
        toast.error("Selecione a nova data de vencimento");
        return;
      }
      body.new_due_date = newDueDate;
    }

    setSaving(true);
    try {
      await api.post(`/contracts/${contract.id}/change-due-day`, body);
      toast.success("Vencimento alterado");
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao alterar vencimento");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
      data-testid="change-due-day-modal"
    >
      <div className="fc-card w-full max-w-lg my-8 p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-display text-lg font-semibold flex items-center gap-2">
              <Calendar size={16} className="text-[#D4AF37]" /> Alterar vencimento
            </h3>
            <p className="text-[12px] text-[#A3A3A3] mt-1">
              Escolha se quer alterar o <strong>dia mensal fixo</strong> do contrato
              ou apenas a <strong>parcela atual</strong>. Pagamentos já feitos não são
              alterados.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-[#737373] hover:text-white"
            data-testid="change-due-day-close"
          >
            <X size={16} />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-md border border-[#1f1f1f] bg-[#0a0a0a] px-3 py-2">
              <div className="text-[10px] uppercase tracking-wider text-[#737373]">
                Dia mensal atual
              </div>
              <div
                className="font-mono-tab text-sm mt-0.5"
                data-testid="current-venc-day"
              >
                {currentVencDay || "—"}
              </div>
            </div>
            <div className="rounded-md border border-[#1f1f1f] bg-[#0a0a0a] px-3 py-2">
              <div className="text-[10px] uppercase tracking-wider text-[#737373]">
                Vencimento do ciclo atual
              </div>
              <div
                className="font-mono-tab text-sm mt-0.5"
                data-testid="current-due-date"
              >
                {currentDueDate ? formatDateBR(currentDueDate) : "—"}
              </div>
            </div>
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-wider text-[#737373] block mb-1">
              Tipo de alteração
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setMode("monthly")}
                className={`flex-1 px-3 py-2 rounded border text-sm transition ${
                  mode === "monthly"
                    ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#E8C547]"
                    : "border-[#262626] text-[#A3A3A3] hover:border-[#404040]"
                }`}
                data-testid="change-due-day-mode-monthly"
              >
                Dia mensal do contrato
              </button>
              <button
                type="button"
                onClick={() => setMode("current_cycle")}
                className={`flex-1 px-3 py-2 rounded border text-sm transition ${
                  mode === "current_cycle"
                    ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#E8C547]"
                    : "border-[#262626] text-[#A3A3A3] hover:border-[#404040]"
                }`}
                data-testid="change-due-day-mode-current"
              >
                Só esta parcela
              </button>
            </div>
            <div className="text-[11px] text-[#737373] mt-1.5">
              {mode === "monthly"
                ? "Afeta próximas parcelas em aberto (opcionalmente também a parcela atual)."
                : "Reagenda apenas o vencimento da parcela atual (não altera o dia mensal do contrato)."}
            </div>
          </div>

          {mode === "monthly" ? (
            <>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-[#737373] block mb-1">
                  Novo dia de vencimento (1-31)
                </label>
                <input
                  type="number"
                  min="1"
                  max="31"
                  required
                  className="fc-input w-full text-[13px]"
                  value={newVencDay}
                  onChange={(e) => setNewVencDay(e.target.value)}
                  placeholder="Ex.: 15"
                  data-testid="new-venc-day-input"
                />
              </div>
              <label className="flex items-center gap-2 text-[12px] text-[#A3A3A3] cursor-pointer">
                <input
                  type="checkbox"
                  checked={applyToCurrent}
                  onChange={(e) => setApplyToCurrent(e.target.checked)}
                  data-testid="apply-to-current-cycle"
                />
                <span>
                  Aplicar também à parcela atual em aberto
                  {currentDueDate && (
                    <span className="text-[#737373]">
                      {" "}
                      (vencimento {formatDateBR(currentDueDate)})
                    </span>
                  )}
                </span>
              </label>
              {previewMonthly && previewMonthly.newDate && (
                <div
                  className="rounded-md border border-[#D4AF37]/30 bg-[#0f0f0f] p-3"
                  data-testid="change-due-day-preview"
                >
                  <div className="text-[10px] uppercase tracking-wider text-[#D4AF37] mb-2">
                    Prévia após salvar
                  </div>
                  <div className="text-[12px] text-[#A3A3A3]">
                    Dia mensal: <span className="text-white font-mono-tab">{previewMonthly.day}</span>
                    {applyToCurrent && (
                      <>
                        {" • "}Nova data do ciclo atual:{" "}
                        <span className="text-white font-mono-tab" data-testid="preview-new-due-date">
                          {formatDateBR(previewMonthly.newDate)}
                        </span>
                        {previewMonthly.capped && (
                          <span className="text-[11px] text-amber-300 block mt-1">
                            (dia ajustado para o último dia disponível do mês)
                          </span>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div>
              <label className="text-[11px] uppercase tracking-wider text-[#737373] block mb-1">
                Nova data de vencimento
              </label>
              <input
                type="date"
                required
                className="fc-input w-full text-[13px]"
                value={newDueDate}
                onChange={(e) => setNewDueDate(e.target.value)}
                data-testid="new-due-date-input"
              />
              {newDueDate && currentDueDate && newDueDate !== currentDueDate && (
                <div className="text-[11px] text-[#A3A3A3] mt-1.5" data-testid="change-due-day-preview">
                  De <span className="font-mono-tab">{formatDateBR(currentDueDate)}</span> →{" "}
                  <span className="font-mono-tab text-white">{formatDateBR(newDueDate)}</span>
                </div>
              )}
            </div>
          )}

          <div>
            <label className="text-[11px] uppercase tracking-wider text-[#737373] block mb-1">
              Motivo (obrigatório, mín. 5 caracteres)
            </label>
            <textarea
              className="fc-input w-full text-[13px]"
              rows={3}
              required
              minLength={5}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ex.: cliente solicitou nova data por mudança do dia do salário"
              data-testid="change-due-day-reason"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="fc-btn-ghost text-xs"
              data-testid="change-due-day-cancel"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="fc-btn-gold flex items-center gap-1.5 text-xs"
              data-testid="change-due-day-submit"
            >
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Calendar size={12} />}
              Confirmar alteração
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
